/**
 * OAuth Callback Page
 *
 * Handles the redirect from Cognito after social login (Google, Apple).
 * Exchanges the authorization code for tokens and syncs user with backend.
 */
import { useSearchParams } from 'react-router-dom';

import { useEffect, useRef, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { exchangeCodeForTokens } from '@splitfiesta/platform-web/auth/cognito';
import type { UserSyncResponseDto } from '@splitfiesta/shared';
import { Alert, Card, H1, H2, Text } from '@splitfiesta/ui';

import { Footer } from '@/components/layout/Footer';

import { useRouter } from '@/hooks/useRouteChangeRouter';

import { trackLogin, trackSignUp } from '@/lib/analytics';
import { queryClient } from '@/lib/queryClient';

import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const API_VERSION = '/api/v1';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const router = useRouter();
  const { t, ready } = useTranslation();
  const { setAuthFromDto } = useAuthStore();

  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  // Prevent double processing in StrictMode
  const hasProcessed = useRef(false);

  // Load required namespaces
  const authLoaded = useNamespace('auth');
  const commonLoaded = useNamespace('common');

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      const code = searchParams.get('code');
      const stateParam = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Parse state parameter to get invite token, language, and provider if present
      let inviteToken: string | undefined;
      let lang: string | undefined;
      let provider: 'google' | 'apple' | undefined;
      if (stateParam) {
        try {
          const state = JSON.parse(stateParam);
          inviteToken = state.invite;
          lang = state.lang;
          provider = state.provider;
        } catch {
          // Invalid state, ignore
        }
      }

      // Handle OAuth error
      if (errorParam) {
        setError(errorDescription || t('auth:errors.socialLoginFailed'));
        setIsProcessing(false);
        return;
      }

      // No code provided
      if (!code) {
        setError(t('auth:errors.socialLoginFailed'));
        setIsProcessing(false);
        return;
      }

      try {
        // Exchange authorization code for tokens
        const tokens = await exchangeCodeForTokens(code);
        if (!tokens?.idToken) {
          throw new Error('No tokens received');
        }

        // Sync user with backend
        // Send empty JSON object - Fastify requires body when Content-Type is application/json
        const response = await fetch(
          `${API_BASE_URL}${API_VERSION}/auth/sync`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokens.idToken}`,
            },
            body: JSON.stringify({}),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to sync user');
        }

        const data = await response.json();
        const syncResult = data.data as UserSyncResponseDto;

        // Track analytics - track as signup for new users, login for returning users
        if (provider) {
          if (syncResult.isNewUser) {
            trackSignUp(provider);
          } else {
            trackLogin(provider);
          }
        }

        // Clear React Query cache
        queryClient.clear();

        // Update auth store (include refresh token for token refresh support)
        setAuthFromDto(syncResult.user, tokens.idToken, tokens.refreshToken);

        // Navigate to invite page if token present, otherwise home
        if (inviteToken) {
          const langQuery = lang ? `?lang=${lang}` : '';
          router.push(`/invite/${inviteToken}${langQuery}`);
        } else {
          router.push('/');
        }
      } catch {
        setError(t('auth:errors.socialLoginFailed'));
        setIsProcessing(false);
      }
    };

    if (ready && authLoaded) {
      handleCallback();
    }
  }, [searchParams, router, setAuthFromDto, t, ready, authLoaded]);

  // Show loading while translations load
  if (!ready || !authLoaded || !commonLoaded) {
    return (
      <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-orange-400 to-red-400 dark:bg-slate-900 dark:bg-none flex flex-col">
      <div className="flex-1 p-4 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <H1 className="text-white drop-shadow-lg mb-2">
              {t('common:app.name')}
            </H1>
          </div>

          <Card>
            {isProcessing ? (
              <div className="text-center py-8">
                <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <H2>{t('common:actions.loading')}</H2>
                <Text variant="bodySmall" className="mt-2">
                  {t('auth:messages.loginSuccess')}
                </Text>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <Alert type="error" message={error} className="mb-6" />
                <button
                  onClick={() => router.push('/login')}
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                >
                  {t('auth:verifyEmail.backToLogin')}
                </button>
              </div>
            ) : null}
          </Card>
        </div>
      </div>
      <Footer variant="minimal" />
    </div>
  );
}
